import React, { useState, useRef } from "react";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import ListView from "../../../components/common/ListView";
// ... other imports (ListView, TableSkeleton, NoDataFound, Section, icons, etc.)
import { useNavigate } from "react-router-dom";

type ShipmentType = "shipments" | "reverted";

const Section = ({
    title,
    children,
    className,
    mainClassName,
}: {
    title?: string;
    className?: string;
    mainClassName?: string;
    children: React.ReactNode;
}) => (
    <div className="p-3 sm:p-4 bg-[#F7F6F2] rounded-2xl sm:rounded-3xl">
        <div className=" ">{children}</div>
    </div>
);

interface PickupShipmentListsProps {
    pickupData: any;
    isLoading: boolean;
    isLoadingMore: boolean;
    pageNo: number;
    handleScroll?: () => void;
    scrollRef?: React.RefObject<HTMLDivElement>;
}

const PickupShipmentLists: React.FC<PickupShipmentListsProps> = ({
    pickupData,
    isLoading,
    isLoadingMore,
    pageNo,
    handleScroll,
    scrollRef,
}) => {
    const [activeTab, setActiveTab] = useState<ShipmentType>("shipments");
    const navigate = useNavigate();

    const getCurrentShipments = () => {
        switch (activeTab) {
            case "shipments":
                return pickupData?.nonRevertedShipments || [];
            case "reverted":
                return pickupData?.revertStatuses || [];
            // case "verified":
            //     return pickupData?.verifiedShipments || [];
            default:
                return [];
        }
    };

    const currentShipments = getCurrentShipments();

    // Count for each type (for tab badges)
    const counts: any = {
        shipments: pickupData?.nonRevertedShipments?.length || 0,
        collected: pickupData?.collectedShipments?.length || 0,
        verified: pickupData?.verifiedShipments?.length || 0,
        reverted: pickupData?.revertStatuses?.length || 0
    };

    return (
        <Section>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                {(["shipments", "reverted"] as ShipmentType[]).map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`
              flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all
              ${activeTab === tab
                                ? "bg-[#FBD037] text-[#000000] shadow-sm"
                                : "bg-gray-100 text-[#212121] hover:bg-gray-200"
                            }
            `}
                    >
                        <span className="capitalize">
                            {tab === "shipments"
                                ? "Shipments"
                                : tab === "reverted"
                                    ? "Reverted"
                                    : "Verified"}
                        </span>
                        <span className="bg-white/70 px-2 py-0.5 rounded-full text-xs">
                            {counts[tab]}
                        </span>
                    </button>
                ))}
            </div>

            {/* Content Card */}
            <div className="border rounded-3xl w-full overflow-hidden bg-white">
                {/* Card Header */}
                <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-200 py-3 px-5 bg-[#F7F6F2]">
                    {activeTab === "shipments"
                        ? "Generated Shipments"
                        : activeTab === "reverted"
                            ? "Reverted Shipments"
                            : "Verified Shipments"}{" "}
                    ({currentShipments.length})
                </h3>

                {/* List or Loading / Empty */}
                {isLoading && pageNo === 1 ? (
                    <div className="p-4">
                        <TableSkeleton
                            rows={6}
                            columns={["AWB", "Origin", "Destination", "Status"]}
                        />
                    </div>
                ) : currentShipments.length > 0 ? (
                    <div className="relative">
                        <div
                            ref={scrollRef}
                            className="max-h-[350px] sm:max-h-[450px] xl:max-h-[550px] overflow-y-auto"
                            onScroll={handleScroll}
                        >
                            <ListView
                                mailDivClassName="w-full p-0"
                                mainDivClassName=""
                                trClassName="hover:bg-[#FBD037]/10 group transition-colors"
                                tdClassName="rounded-r-none rounded-l-none px-5 py-3"
                                data={currentShipments}
                                onRowClick={(item) => console.log("Clicked:", item)}
                                columns={[
                                    {
                                        label: "AWB",
                                        render: (c: any) => (
                                            <span
                                                className="text-black hover:text-blue-800 hover:underline cursor-pointer font-medium transition-colors"
                                                onClick={() => {
                                                    if (c.awbNumber) {
                                                        navigate(`/orders/${c.awbNumber}`);
                                                    }
                                                }}
                                            >
                                                {c.awbNumber || "—"}
                                            </span>
                                        ),
                                    },
                                    { label: "Origin", render: (c: any) => c.senderCity },
                                    {
                                        label: "Destination",
                                        children: [
                                            { label: "Country", render: (c: any) => c.receiverCountry },
                                            { label: "City", render: (c: any) => c.receiverCity },
                                            { label: "District", render: (c: any) => c.receiverDistrict },
                                        ],
                                    },
                                    {
                                        label: "Status",
                                        render: (c: any) => {
                                            const getStatusClasses = (status: string) => {
                                                switch (status?.toUpperCase()) {
                                                    case "OUT FOR DELIVERY":
                                                    case "DISPATCH":
                                                        return "bg-[#EBF7CE] text-[#2E7D32] border border-[#C6E59D]";
                                                    case "DELIVERED":
                                                        return "bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7]";
                                                    case "RETURN_TO_SHIPPER":
                                                        return "bg-[#FFECB3] text-[#F57C00] border border-[#FFE082]";
                                                    case "UNDELIVERED":
                                                        return "bg-[#FFEBEE] text-[#C62828] border border-[#EF9A9A]";
                                                    default:
                                                        return "bg-gray-100 text-gray-700 border border-gray-300";
                                                }
                                            };

                                            const formattedStatus = c.status
                                                ?.toLowerCase()
                                                ?.replace(/_/g, " ")
                                                ?.replace(/\b\w/g, (char: string) => char.toUpperCase()) || "Unknown";

                                            return (
                                                <div className="flex justify-center">
                                                    <span
                                                        className={`inline-block rounded-full px-4 py-1 text-xs font-medium ${getStatusClasses(
                                                            c.status
                                                        )}`}
                                                    >
                                                        {formattedStatus}
                                                    </span>
                                                </div>
                                            );
                                        },
                                    },
                                ]}
                            />

                            {isLoadingMore && (
                                <div className="py-6 text-center text-sm text-gray-500 bg-gray-50">
                                    Loading more shipments...
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="py-12">
                        <NoDataFound />
                    </div>
                )}
            </div>
        </Section>
    );
};

export default PickupShipmentLists;